import { EnrichmentPriceItem } from "@app/shared/models/enrichment-detail.model";

/**
 * Selling window ↔ ValidFor.
 *
 * `prices[i].validFor` is the catalog-owned period the price is in effect;
 * the Selling window (`effectiveStartDate` / `effectiveEndDate`) is the
 * author-chosen period INSIDE it, edited on the Pricing tab's Availability
 * card under a voice channel.
 *
 * These helpers live here rather than inside the Pricing component because
 * TWO paths now read them and they must agree to the byte:
 *   - the DISPLAY path — the date pickers, which show the ValidFor bound
 *     whenever the author has set no date of their own, and
 *   - the SAVE path — {@link seedSellingWindowFromValidFor}, which writes that
 *     same shown value into the outgoing body so the window the author was
 *     looking at is the window that gets stored.
 * A second, subtly different parse/format rule on either side would put the
 * author back where they started: reading one window and saving another.
 */

/** ISO date-time carrying an explicit zone (`Z` or `±hh:mm`). */
export const ZONED_ISO_PATTERN =
  /^\d{4}-\d{2}-\d{2}T[\d:.]+(?:Z|[+-]\d{2}:?\d{2})$/i;

/** Bare (zone-less) date / date-time shapes the CIVR overlay stores. */
const CIVR_DATE_PATTERN =
  /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2}))?)?$/;

/**
 * Parse a CIVR overlay date or a catalog `validFor` bound into a Date.
 *
 * A bare `YYYY-MM-DD[THH:mm[:ss]]` is read in LOCAL terms — `new Date()` alone
 * treats the date-only form as UTC midnight, which renders as 07:00 of the
 * same day for an ICT (+07) author and, worse, compares an hour-shifted
 * instant against the window bounds. Anything else (an ISO string with a zone,
 * which is what the BE ships in `validFor`) falls through to `Date`, whose
 * instant is already unambiguous.
 *
 * Returns `undefined` for absent / empty / unparseable input so every caller
 * can treat "no usable bound" as "unbounded".
 */
export function parseCivrDate(value: string | undefined): Date | undefined {
  if (!value) return undefined;
  const parts = CIVR_DATE_PATTERN.exec(value.trim());
  const parsed = parts
    ? new Date(
        Number(parts[1]),
        Number(parts[2]) - 1,
        Number(parts[3]),
        Number(parts[4] ?? 0),
        Number(parts[5] ?? 0),
        Number(parts[6] ?? 0),
      )
    : new Date(value);
  return Number.isNaN(parsed.getTime()) ? undefined : parsed;
}

/**
 * Date → the stored CIVR string, in LOCAL components (never `toISOString()`).
 * Midnight serialises date-only, so a window the author sets without a time
 * keeps the exact `YYYY-MM-DD` wire shape it had before the picker gained a
 * clock; any other time appends `THH:mm:ss`.
 */
export function formatCivrDate(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  const day = `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
  const h = date.getHours();
  const m = date.getMinutes();
  const sec = date.getSeconds();
  if (!h && !m && !sec) return day;
  return `${day}T${pad(h)}:${pad(m)}:${pad(sec)}`;
}

/** A bound the author has NOT set. `""` is what clearing a picker writes, and
 *  a clear means "go back to the ValidFor" — a voice price has no meaningful
 *  "no window" state, so an emptied bound re-seeds rather than shipping
 *  half-open. */
const isUnset = (value: string | undefined): boolean => !value;

/**
 * Fill an unset Selling window bound from the price's own ValidFor.
 *
 * The author is SHOWN the ValidFor in the pickers whenever they have set no
 * date of their own; without this the price then saves with no window at all,
 * so the card reads complete and the stored value is empty. Applied to the
 * outgoing body on a voice save, this makes the two agree.
 *
 * Rules, per bound, independently:
 *   - authored (non-empty) → untouched, always. The seed never overrides.
 *   - unset or cleared → taken from the matching `validFor` bound.
 *   - no `validFor` bound, or an unparseable one → left alone; a price the
 *     catalog does not bound gets no window invented for it.
 *
 * PURE, and deliberately so: it returns a NEW price when it seeds anything and
 * the SAME reference when it does not. `savePrices` reuses its price array for
 * both the post-save merge and the fallback for prices whose PATCH failed — an
 * in-place seed would leave a failed save displaying dates the backend never
 * received, which is the original bug wearing a different hat.
 *
 * The caller owns the channel gate; this function has no opinion about which
 * channels want a Selling window.
 */
export function seedSellingWindowFromValidFor(
  price: EnrichmentPriceItem,
): EnrichmentPriceItem {
  const start = isUnset(price.effectiveStartDate)
    ? parseCivrDate(price.validFor?.startDateTime)
    : undefined;
  const end = isUnset(price.effectiveEndDate)
    ? parseCivrDate(price.validFor?.endDateTime)
    : undefined;

  if (!start && !end) return price;

  const seeded: EnrichmentPriceItem = { ...price };
  if (start) seeded.effectiveStartDate = formatCivrDate(start);
  if (end) seeded.effectiveEndDate = formatCivrDate(end);
  return seeded;
}
